import { Plus, MessageSquare, Trash2, Shield } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useV2Conversations,
  useCreateV2Conversation,
  useDeleteV2Conversation,
} from '@/hooks/use-conversations-v2'
import { cn } from '@/lib/utils'
import { formatDistanceToNow } from 'date-fns'

interface ConversationSidebarProps {
  activeStreamingId?: string
}

export function ConversationSidebar({ activeStreamingId }: ConversationSidebarProps) {
  const navigate = useNavigate()
  const { conversationId } = useParams()
  const { data } = useV2Conversations()
  const createConversation = useCreateV2Conversation()
  const deleteConversation = useDeleteV2Conversation()

  const conversations = data?.conversations ?? []

  const handleNew = async () => {
    const result = await createConversation.mutateAsync({})
    navigate(`/polix-ai/${result.id}`)
  }

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    deleteConversation.mutate(id)
    if (conversationId === id) navigate('/polix-ai')
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-muted/30">
      {/* Header */}
      <div className="p-4 pb-3">
        <div className="flex items-center gap-2 mb-4 px-1">
          <Shield className="size-4 text-primary" strokeWidth={1.5} />
          <span className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">
            Conversations
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="w-full justify-start gap-2 h-9 bg-background/80 hover:bg-background border-border/60"
          onClick={handleNew}
        >
          <Plus className="size-3.5" />
          <span className="text-sm">New conversation</span>
        </Button>
      </div>

      {/* Conversation List */}
      <ScrollArea className="flex-1 px-2">
        <div className="space-y-0.5 pb-4">
          {conversations.length === 0 && (
            <p className="text-xs text-muted-foreground/60 text-center py-8 px-4">
              No conversations yet
            </p>
          )}
          {conversations.map((c) => {
            const isActive = conversationId === c.id
            return (
              <button
                key={c.id}
                onClick={() => navigate(`/polix-ai/${c.id}`)}
                className={cn(
                  'group w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-[background-color,color] duration-150',
                  isActive
                    ? 'bg-primary/8 text-foreground'
                    : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground'
                )}
              >
                {activeStreamingId === c.id ? (
                  <span className="relative flex size-3.5 shrink-0">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex size-3.5 rounded-full bg-green-500" />
                  </span>
                ) : (
                  <MessageSquare
                    className={cn(
                      'size-3.5 shrink-0 transition-colors',
                      isActive ? 'text-primary' : 'text-muted-foreground/50'
                    )}
                    strokeWidth={1.5}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <span className="block text-sm truncate leading-tight">
                    {c.lastMessage || c.contextType || 'New conversation'}
                  </span>
                  <span className="block text-[11px] text-muted-foreground/60 mt-0.5">
                    {formatDistanceToNow(new Date(c.createdAt), {
                      addSuffix: true,
                    })}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="size-6 p-0 shrink-0 text-muted-foreground/40 hover:text-destructive transition-colors"
                  onClick={(e) => handleDelete(e, c.id)}
                >
                  <Trash2 className="size-3" />
                </Button>
              </button>
            )
          })}
        </div>
      </ScrollArea>
    </div>
  )
}
